/**
 * SqlExecutor — the thin SQL seam the SqliteRepo runs on. A Capacitor-backed
 * executor powers the native iOS/Android build; a sql.js-backed one (test-only)
 * lets the shared Repo conformance suite run against the SQLite adapter
 * headlessly, guaranteeing parity with the Dexie adapter.
 */
export interface SqlExecutor {
  /** Run DDL / statements with no bound params (may contain several `;`-separated). */
  exec(sql: string): Promise<void>;
  /** Run a single write statement with bound params. */
  run(sql: string, params?: unknown[]): Promise<void>;
  /** Run a query and return rows as plain objects. */
  query<T = Record<string, unknown>>(sql: string, params?: unknown[]): Promise<T[]>;
  /** Run a unit of work inside a transaction. */
  tx(fn: () => Promise<void>): Promise<void>;
}

/**
 * CapacitorSqlExecutor — wraps @capacitor-community/sqlite (native SQLite in the
 * app's filesystem sandbox; survives WKWebView storage eviction). Lazily opens
 * one connection. The plugin module is imported dynamically so this file is
 * safe to bundle for the web build (where the SQLite adapter is never selected).
 */
export class CapacitorSqlExecutor implements SqlExecutor {
  private dbName: string;
  // Typed loosely to avoid a hard compile dep on the plugin's types in the web build.
  private conn: any = null;
  private opening: Promise<unknown> | null = null;
  // True while a manual transaction (tx) is open. The plugin's run()/execute()
  // default to `transaction: true` (each wraps itself in BEGIN…COMMIT), which
  // would collide with our manual beginTransaction(); so inside a tx we pass
  // `transaction: false` and let tx() do the single commit.
  private inTx = false;
  // One SQLite connection can't run concurrent transactions, and the warm-up
  // fires several applyDelta()s at once. This lock serializes every operation
  // (except the inner statements of a tx, which run under the tx's held lock).
  private lock: Promise<void> = Promise.resolve();

  constructor(dbName = "icheck-offline") {
    this.dbName = dbName;
  }

  private async db(): Promise<any> {
    if (this.conn) return this.conn;
    if (!this.opening) {
      this.opening = (async () => {
        const mod: any = await import("@capacitor-community/sqlite");
        const sqlite = new mod.SQLiteConnection(mod.CapacitorSQLite);
        const isConn = (await sqlite.isConnection(this.dbName, false)).result;
        this.conn = isConn
          ? await sqlite.retrieveConnection(this.dbName, false)
          : await sqlite.createConnection(this.dbName, false, "no-encryption", 1, false);
        await this.conn.open();
        return this.conn;
      })();
    }
    await this.opening;
    return this.conn;
  }

  /** Serialize an operation on the single connection. Statements issued from
   *  inside a tx run directly (the tx already holds the lock). */
  private async locked<T>(op: () => Promise<T>): Promise<T> {
    if (this.inTx) return op();
    const prev = this.lock;
    let release!: () => void;
    this.lock = new Promise<void>((r) => { release = r; });
    await prev;
    try {
      return await op();
    } finally {
      release();
    }
  }

  exec(sql: string): Promise<void> {
    return this.locked(async () => {
      const db = await this.db();
      await db.execute(sql, !this.inTx);
    });
  }
  run(sql: string, params: unknown[] = []): Promise<void> {
    return this.locked(async () => {
      const db = await this.db();
      await db.run(sql, params, !this.inTx);
    });
  }
  query<T = Record<string, unknown>>(sql: string, params: unknown[] = []): Promise<T[]> {
    return this.locked(async () => {
      const db = await this.db();
      const res = await db.query(sql, params);
      return (res.values ?? []) as T[];
    });
  }
  tx(fn: () => Promise<void>): Promise<void> {
    return this.locked(async () => {
      const db = await this.db();
      this.inTx = true;
      await db.beginTransaction();
      try {
        await fn();
        await db.commitTransaction();
      } catch (err) {
        try { await db.rollbackTransaction(); } catch { /* ignore */ }
        throw err;
      } finally {
        this.inTx = false;
      }
    });
  }
}
